import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useField } from 'formik';
import {
  Select as ShadSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import { cn } from '@dashboard/lib/utils';

const EMPTY_VALUE_SENTINEL = '__cp_empty_value__';
// Radix Select works with string values only. Stringify so numeric option values
// (e.g. font sizes / padding) round-trip correctly: Radix always reports the
// selected value back as a string, so the trigger and items must speak strings too.
const toSentinel = v =>
  v === '' || v === undefined || v === null ? EMPTY_VALUE_SENTINEL : String(v);
const fromSentinel = v => (v === EMPTY_VALUE_SENTINEL ? '' : v);

// Backwards-compat: many call sites still pass <option> JSX children.
// Convert them to the {value, label, disabled} shape the new Select expects.
// If the FIRST option has an empty value, treat its label as the placeholder hint
// (native <select> renders this option as the default visible label).
const childrenToOptions = children => {
  const options = [];
  let placeholderHint;
  let isFirst = true;
  React.Children.forEach(children, child => {
    if (!React.isValidElement(child)) return;
    if (child.type === 'option') {
      const opt = {
        value: child.props.value,
        label: child.props.children,
        disabled: child.props.disabled,
      };
      if (isFirst && opt.value === '') {
        placeholderHint = opt.label;
      } else {
        options.push(opt);
      }
      isFirst = false;
    } else if (child.type === 'optgroup') {
      React.Children.forEach(child.props.children, grand => {
        if (React.isValidElement(grand) && grand.type === 'option') {
          options.push({
            value: grand.props.value,
            label: grand.props.children,
            disabled: grand.props.disabled,
          });
        }
        isFirst = false;
      });
    }
  });
  return { options, placeholderHint };
};

const Select = ({
  className,
  disabled,
  value,
  onChange,
  name,
  id,
  options,
  placeholder,
  required,
  emptyMessage,
  children,
}) => {
  // A callback ref, not useRef: the list's container has to be known on a render
  // that happens BEFORE it opens, and Radix's own open state does not re-render
  // this component.
  const [trigger, setTrigger] = useState(null);

  const { resolvedOptions, resolvedPlaceholder } = useMemo(() => {
    if (options && options.length > 0) {
      return { resolvedOptions: options, resolvedPlaceholder: placeholder };
    }
    if (children) {
      const { options: parsed, placeholderHint } = childrenToOptions(children);
      return {
        resolvedOptions: parsed,
        resolvedPlaceholder: placeholder ?? placeholderHint,
      };
    }
    return { resolvedOptions: [], resolvedPlaceholder: placeholder };
  }, [options, children, placeholder]);

  const handleValueChange = useCallback(
    nextValue => {
      if (!onChange) return;
      onChange({ target: { name, value: fromSentinel(nextValue) } });
    },
    [name, onChange],
  );

  // Forward synthetic `change` events fired on the trigger (e.g. by
  // @testing-library/fireEvent.change) into the same handler. This keeps
  // imperative test interactions working without rendering a duplicate
  // <select> in the accessibility tree.
  const handleTriggerChange = useCallback(
    e => {
      if (!onChange) return;
      onChange({ target: { name, value: e.target.value } });
    },
    [name, onChange],
  );

  // Match native <select> behavior: if value is undefined or doesn't match any
  // option, fall back to the first option visually. Skip the fallback when a
  // placeholder is set — that signals "no value yet, prompt the user".
  // Compare loosely (by string form) so a numeric option value matches the
  // string Radix reports back, mirroring native <select> coercion.
  const valueMatchesOption =
    value !== undefined &&
    value !== null &&
    resolvedOptions.some(o => String(o.value) === String(value));
  const shouldFallBack = !valueMatchesOption && !resolvedPlaceholder && resolvedOptions.length > 0;
  const effectiveValue = shouldFallBack ? resolvedOptions[0].value : value;
  const isControlled = effectiveValue !== undefined;
  // An unmatched value with a placeholder must RENDER the placeholder: Radix
  // only shows it for a root value of '' (the documented "clear" state) — the
  // empty-value sentinel matches no item and would leave the trigger blank.
  // A selectable empty option still round-trips through the sentinel (it
  // matches via valueMatchesOption).
  const selectProps = isControlled
    ? { value: !valueMatchesOption && resolvedPlaceholder ? '' : toSentinel(effectiveValue) }
    : {};
  const triggerValue = effectiveValue ?? '';

  return (
    <ShadSelect {...selectProps} onValueChange={handleValueChange} disabled={disabled} name={name}>
      <SelectTrigger
        ref={setTrigger}
        id={id}
        name={name}
        value={triggerValue}
        onChange={handleTriggerChange}
        aria-required={required || undefined}
        className={cn('w-full', className)}
      >
        <SelectValue placeholder={resolvedPlaceholder} />
      </SelectTrigger>
      {/* Inside a modal dialog the list has to portal into the dialog: the two
          packages resolve different react-remove-scroll copies, so a list left on
          document.body is wheel-locked by the dialog and cannot be scrolled. */}
      <SelectContent container={trigger?.closest('[role="dialog"]') ?? undefined}>
        {resolvedOptions.length === 0 && emptyMessage && (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">{emptyMessage}</div>
        )}
        {resolvedOptions.map(option => (
          <SelectItem
            key={option.value}
            value={toSentinel(option.value)}
            disabled={option.disabled}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </ShadSelect>
  );
};

export const SelectField = ({ name, transformValue, onChange, options, ...props }) => {
  const [field, , helpers] = useField(name);
  const setValueRef = useRef(helpers.setValue);
  setValueRef.current = helpers.setValue;

  const handleChange = useCallback(
    async e => {
      const newValue = e.target.value;
      const shouldProceed = (await onChange?.(e)) ?? true;
      if (shouldProceed) {
        const val = transformValue ? transformValue(newValue) : newValue;
        setValueRef.current(val);
      }
    },
    [onChange, transformValue],
  );

  return (
    <Select {...props} name={name} value={field.value} onChange={handleChange} options={options} />
  );
};

export default React.memo(Select);
